import _ from "lodash";
import React from "react";
import classes from "./DropDown.scss";
import { MoreIcon } from "SvgComponents";
import { useTranslation } from "react-i18next";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { getSettingValue } from "modules/PermissionModule";
import { getIsCurrentAcademicYear } from "Platform/modules/PlatformModule";
import { Dropdown, DropdownMenu, IconButton } from "@toddle-edu/ds-web";
import {
  CalendarScheduleOutlined,
  DeleteOutlined,
  InProgressThumbsupOutlined,
  DotsVerticalOutlined,
  PencilLineOutlined,
  PublishOutlined,
  ApprovalTickOutlined,
} from "@toddle-edu/ds-icons";
import classNames from "classnames";
import {
  isDPSubjectJournalEnabled,
  isPortfolioParityEnabled,
  isPortfolioReviewSetupEnabled,
  isPortfolioReviewSetupEnabledAndIsReviewRoute,
} from "AppComponents/Post/utils";
import { updateFilterObject } from "Journal/modules/JournalModule";
import { showActivityTracking } from "../../../utils";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";

const DropDown = React.memo(props => {
  const {
    id,
    createdByType,
    onPostActionClick,
    userType,
    canStudentEditPostOncePublished,
    getIsCurrentAcademicYear,
    isArchivedClass,
    isAddLEToJournal,
    postState,
    showNewDropdown = false, // Prop if we want dropdown options of journal revamp
    curriculumProgramType,
    showBackdateOption = false,
    onEditDateClick,
    studentActivityType,
    isPostApprovalEnabled = true,
    onCloseClick,
    toggleMediaModal,
    isSidePaneContent,
    hideDraftOption = false,
    openedFromNotification = false,
    containerStyle = {},
    isUserAllowedToPublishPost,
    createdById,
    userId,
    isProjectPost,
  } = props;
  const isCurrentAcademicYear = getIsCurrentAcademicYear();
  const { t } = useTranslation(["common", "journal"]);
  const isCreatedByStudent = createdByType == "student";
  const teacherMenuItems = [
    {
      key: "POST_EDIT",
      label: t("edit_post"),
      clickAction: () => {
        onPostActionClick({
          id: id,
          action: "POST_EDIT",
          isAddLEToJournal: isAddLEToJournal,
          params: {
            ...(!_.isNil(studentActivityType) ? { studentActivityType } : {}),
          },
        });
      },
      svg: null,
      isDisable: false,
      icon: <PencilLineOutlined />,
    },
    {
      key: "IN_APPROVAL",
      label: isCreatedByStudent
        ? !isDPSubjectJournalEnabled(curriculumProgramType) ||
          isPortfolioParityEnabled()
          ? t("move_to_in_approval")
          : t("save_as_draft")
        : t("save_as_draft"),
      clickAction: () => {
        onPostActionClick({
          id: id,
          action: isCreatedByStudent
            ? !isDPSubjectJournalEnabled(curriculumProgramType) ||
              isPortfolioParityEnabled()
              ? "IN_APPROVAL"
              : "DRAFT"
            : "DRAFT",
        });
      },
      svg: null,
      isDisable: isArchivedClass,
      icon: isCreatedByStudent ? (
        !isDPSubjectJournalEnabled(curriculumProgramType) ? (
          <InProgressThumbsupOutlined /> // in-approval icon
        ) : (
          <ApprovalTickOutlined /> // draft icon
        )
      ) : (
        <ApprovalTickOutlined /> // draft icon
      ),
    },
    {
      key: "POST_DELETE",
      label: t("delete"),
      clickAction: () => {
        onPostActionClick({ id: id, action: "DELETE" });
      },
      svg: null,
      isDisable: isArchivedClass,
      isDestructive: true,
      icon: <DeleteOutlined variant="critical" />,
    },
  ];

  const studentMenuItems = [
    {
      key: "POST_EDIT",
      label: t("edit_post"),
      clickAction: () => {
        onPostActionClick({
          id: id,
          action: "POST_EDIT",
          isAddLEToJournal: isAddLEToJournal,
        });
      },
      svg: null,
      isDisable: false,
      icon: <PencilLineOutlined />,
    },
    {
      key: "POST_DELETE",
      label: t("delete"),
      clickAction: () => {
        onPostActionClick({ id: id, action: "DELETE" });
      },
      svg: null,
      isDisable: isArchivedClass,
      isDestructive: true,
      icon: <DeleteOutlined variant="critical" />,
    },
  ];

  if (
    userType === "student" &&
    ((!canStudentEditPostOncePublished && _.isEqual(postState, "PUBLISHED")) ||
      !isCurrentAcademicYear)
  ) {
    return null;
  }

  if (showBackdateOption == true) {
    const backDateOption = {
      key: "POST_EDIT_DATE",
      label: t("edit_with_label", { label: t("date") }),
      clickAction: () => {
        onEditDateClick();
      },
      icon: <CalendarScheduleOutlined />,
    };
    teacherMenuItems.splice(2, 0, { ...backDateOption });
    studentMenuItems.splice(1, 0, { ...backDateOption });
  }

  if (showNewDropdown) {
    const state = ["SCHEDULED", "PUBLISHED"];
    if (
      !_.includes(state, postState) ||
      (isCreatedByStudent && !isPostApprovalEnabled) ||
      (hideDraftOption && _.includes(["DRAFT"], postState)) ||
      isProjectPost
    ) {
      _.remove(
        teacherMenuItems,
        menuItem => _.get(menuItem, "key", "") === "IN_APPROVAL"
      );
    }
  }

  if (
    !_.isNil(studentActivityType) &&
    !showActivityTracking() &&
    !isPortfolioReviewSetupEnabledAndIsReviewRoute()
  ) {
    _.remove(teacherMenuItems, item => _.get(item, "key") == "POST_EDIT");
  }
  if (
    _.isEqual(postState, "DRAFT") &&
    isSidePaneContent &&
    (isPortfolioReviewSetupEnabled() ? isUserAllowedToPublishPost : true)
  ) {
    const publishOption = {
      key: "PUBLISH",
      label: t("publish"),
      clickAction: () => {
        onPostActionClick({ id: id, action: "PUBLISH" });
      },
      icon: <PublishOutlined />,
    };
    teacherMenuItems.splice(0, 0, publishOption);
  } else if (
    _.isEqual(postState, "DRAFT") &&
    isSidePaneContent &&
    isPortfolioReviewSetupEnabled() &&
    !isUserAllowedToPublishPost
  ) {
    const canUserEditPost =
      createdById === userId ? true : isUserAllowedToPublishPost;
    const publishOption = {
      key: "SEND_FOR_INAPPROVAL",
      label: t("journal:send_for_approval"),
      clickAction: () => {
        onPostActionClick({ id: id, action: "IN_APPROVAL" });
      },
      icon: <PublishOutlined />,
      disabled: !canUserEditPost,
      disabledTooltip: !canUserEditPost
        ? t("journal:you_dont_have_permission_publish_post")
        : "",
    };
    teacherMenuItems.splice(0, 0, publishOption);
  }

  const onMenuItemClick = menuItem => {
    menuItem?.domEvent?.stopPropagation();
    const selectedMenuData =
      userType == "student" ? studentMenuItems : teacherMenuItems;
    selectedMenuData.find(item => item.key == menuItem.key).clickAction();
    if (showBackdateOption && menuItem.key === "POST_EDIT_DATE") return; // Don't close theatre mode if backdate option is clicked
    if (
      _.isFunction(onCloseClick) &&
      isSidePaneContent &&
      !openedFromNotification
    )
      onCloseClick();
    if (_.isFunction(toggleMediaModal) && isSidePaneContent)
      toggleMediaModal(false);
  };

  const dropdownClass = classNames({
    [classes.dropdown]: !showNewDropdown,
  });

  return (
    <div className={classes.container} style={containerStyle}>
      {/* Show different dropdown items based on user type */}
      <Dropdown
        className={dropdownClass}
        onClick={e => e.stopPropagation()}
        overlay={
          <DropdownMenu
            onClick={onMenuItemClick}
            options={
              userType == "student"
                ? isPortfolioParityEnabled() && !_.isEqual(postState, "DRAFT")
                  ? _.filter(
                      studentMenuItems,
                      item => item.key !== "POST_DELETE"
                    )
                  : studentMenuItems
                : teacherMenuItems
            }
            testId="journal-feed-post"
          />
        }
      >
        <IconButton
          dsVersion="2.0"
          icon={showNewDropdown ? <DotsVerticalOutlined /> : <MoreIcon />}
          size={showNewDropdown ? "medium" : "small"}
          _legacy_variant="plain"
          type="plain"
          variant="neutral"
          testId="journal-feed-post-dropdown"
        />
      </Dropdown>
    </div>
  );
});

DropDown.displayName = "DropDown";

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  const canStudentEditPostOncePublished =
    getSettingValue({
      name: "EnablePostEditingOncePublishedByStudents",
      courseId: state.teacher.selected_class.selected_course,
      organizationId: userInfo.org_id,
      userId: userInfo.id,
      userEntityType: userInfo.userEntityType,
    }) || _.get(ownProps, "isProjectPost", false);
  const curriculumProgramType = _.get(
    state,
    "platform.currentCurriculumProgram.type"
  );
  return {
    canStudentEditPostOncePublished,
    curriculumProgramType,
    userId: userInfo?.id,
  };
};

const mapActionCreators = {
  getIsCurrentAcademicYear,
  updateFilterObject,
};

export default compose(connect(mapStateToProps, mapActionCreators))(DropDown);
